import React, { useState, useRef, useEffect } from 'react';
import { useSite } from '../context/SiteContext';

const SiteCombobox = () => {
  const { sites, selectedSite, setSelectedSite, loading } = useSite();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSites, setFilteredSites] = useState([]);
  const comboboxRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const filtered = sites.filter(site => 
      site.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      site.city.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredSites(filtered);
  }, [sites, searchTerm]);

  useEffect(() => {
    const selectedSiteObj = sites.find(site => site._id === selectedSite);
    if (selectedSiteObj) {
      setSearchTerm(selectedSiteObj.name);
    } else if (selectedSite === 'all') {
      setSearchTerm('All Sites');
    }
  }, [selectedSite, sites]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (comboboxRef.current && !comboboxRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) return null;

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleSiteSelect = (siteId, siteName) => {
    setSelectedSite(siteId);
    setSearchTerm(siteName);
    setIsOpen(false);
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('siteChanged', { detail: { siteId, siteName } }));
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredSites.length > 0) {
        const firstSite = filteredSites[0];
        handleSiteSelect(firstSite._id, firstSite.name);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative w-48" ref={comboboxRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search sites..."
          className="w-full px-3 py-2 pr-8 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          aria-label="Toggle dropdown"
        >
          <svg 
            className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      
      {isOpen && (
        <div 
          className="fixed top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-[99999]"
          role="listbox"
          style={{
            left: comboboxRef.current?.getBoundingClientRect().left || 0,
            width: comboboxRef.current?.getBoundingClientRect().width || 'auto',
            top: (comboboxRef.current?.getBoundingClientRect().bottom || 0) + 4
          }}
        >
          <div
            onClick={() => handleSiteSelect('all', 'All Sites')}
            className={`px-3 py-2 hover:bg-teal-50 cursor-pointer text-sm ${
              selectedSite === 'all' ? 'bg-teal-50 text-teal-700' : 'text-gray-700'
            }`}
            role="option"
            aria-selected={selectedSite === 'all'}
          >
            <div className="font-medium">All Sites</div>
            <div className="text-xs text-gray-500">Show all sites</div>
          </div>
          {filteredSites.map(site => (
            <div
              key={site._id}
              onClick={() => handleSiteSelect(site._id, site.name)}
              className={`px-3 py-2 hover:bg-teal-50 cursor-pointer text-sm ${
                selectedSite === site._id ? 'bg-teal-50 text-teal-700' : 'text-gray-700'
              }`}
              role="option"
              aria-selected={selectedSite === site._id}
            >
              <div className="font-medium">{site.name}</div>
              <div className="text-xs text-gray-500">{site.city}</div>
            </div>
          ))}
          {filteredSites.length === 0 && searchTerm && searchTerm !== 'All Sites' && (
            <div className="px-3 py-2 text-sm text-gray-500">
              No sites found matching "{searchTerm}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SiteCombobox;